
import React from 'react';
import { useLanguage } from '../lib/LanguageContext';
import { Mail, Phone, MapPin, Building2, ExternalLink } from 'lucide-react';

export default function Contact() {
  const { t } = useLanguage();

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    
    fetch('https://api.dev.auxo.dk/webform/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(() => {
      alert(t.ui.successMessage);
      e.target.reset();
    });
  };

  return (
    <div className="bg-white min-h-screen">
      <div className="relative bg-slate-950 py-12 md:py-20 lg:py-32 text-white overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/kundes%20img/Service-2-1030x773.jpg" 
            alt="" 
            className="w-full h-full object-cover opacity-20 brightness-50"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-transparent to-transparent" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-extrabold mb-6 tracking-tight border-l-4 border-[#f59c01] pl-4 inline-block">
              {t.contact.title}
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 leading-relaxed">
              {t.contact.subtitle}
            </p>
          </div>
        </div>
      </div>

      <section className="py-12 md:py-16 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Info */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-8 border-l-4 border-[#f59c01] pl-4 inline-block">
                {t.ui.findUs}
              </h2>
              
              <div className="space-y-8 mb-12">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 text-[#f59c01]">
                    <Building2 className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">{t.company}</h3>
                    <p className="text-slate-600 text-xs sm:text-base">CVR: {t.cvr}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 text-[#f59c01]">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">{t.ui.addressLabel}</h3>
                    <p className="text-slate-600 text-xs sm:text-base">{t.address}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 text-[#f59c01]">
                    <Phone className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">{t.ui.phoneLabel}</h3>
                    <a href={`tel:${t.phone.replace(/\s/g, '')}`} className="text-[#f59c01] hover:underline font-medium text-xs sm:text-base">{t.phone}</a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 text-[#f59c01]">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Email</h3>
                    <a href={`mailto:${t.email}`} className="text-[#f59c01] hover:underline font-medium text-xs sm:text-base">{t.email}</a>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl overflow-hidden shadow-lg border border-slate-200 p-6 flex flex-col items-center text-center bg-slate-50">
                 <img 
                  src="/images/kundes%20img/Karsten-258x300.jpg" 
                  alt="Karsten Andersen" 
                  className="w-40 h-40 rounded-full object-cover mb-4 border-4 border-white shadow-md"
                />
                <div>
                  <p className="font-bold text-slate-900 text-lg">Karsten Andersen</p>
                  <p className="text-sm text-slate-500">{t.ui.contactMessage}</p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="relative overflow-hidden bg-slate-900 p-8 md:p-12 rounded-3xl border border-slate-800 shadow-2xl group">
              {/* Background Accents */}
              <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#f59c01] opacity-[0.07] blur-[100px] rounded-full transition-opacity group-hover:opacity-10" />
              <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-[#f59c01] opacity-[0.05] blur-[100px] rounded-full transition-opacity group-hover:opacity-10" />
              
              <div className="relative z-10">
                <h2 className="text-2xl font-bold text-white mb-8 border-l-4 border-[#f59c01] pl-4 inline-block">
                  {t.ui.sendMessage}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <input type="text" name="website_url" className="sr-only absolute" tabIndex="-1" />
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">
                      {t.ui.nameLabel}
                    </label>
                    <input
                      required
                      type="text"
                      name="name"
                      className="w-full bg-slate-800/50 border border-slate-700 text-white px-4 py-3 rounded-lg focus:ring-2 focus:ring-[#f59c01] focus:border-transparent transition-all outline-none placeholder:text-slate-600"
                      placeholder={t.ui.namePlaceholder}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">
                      {t.ui.contactLabel}
                    </label>
                    <input
                      required
                      type="text"
                      name="contact"
                      className="w-full bg-slate-800/50 border border-slate-700 text-white px-4 py-3 rounded-lg focus:ring-2 focus:ring-[#f59c01] focus:border-transparent transition-all outline-none placeholder:text-slate-600"
                      placeholder={t.ui.contactPlaceholder}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">
                      {t.ui.messageLabel}
                    </label>
                    <textarea
                      required
                      name="message"
                      rows="5"
                      className="w-full bg-slate-800/50 border border-slate-700 text-white px-4 py-3 rounded-lg focus:ring-2 focus:ring-[#f59c01] focus:border-transparent transition-all outline-none resize-none placeholder:text-slate-600"
                      placeholder={t.ui.messagePlaceholder}
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-[#f59c01] text-white font-bold py-2.5 md:py-4 rounded-lg hover:bg-[#e08e01] transition-all shadow-lg hover:shadow-[#f59c01]/20 active:scale-[0.98] text-sm md:text-base"
                  >
                    {t.ui.submitButton}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
